import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Timer } from "lucide-react";

const IDLE_TIMEOUT = 30 * 60 * 1000;  // 30 minutes
const REAUTH_TIMEOUT = 30;             // 30 seconds to enter password
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

export default function SessionTimeout() {
  const { user, logout } = useAuth();
  const [showReauth, setShowReauth] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(REAUTH_TIMEOUT);

  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (!user) return;
    if (showReauth) return; // Don't reset while reauth dialog is open
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      setShowReauth(true);
      setPassword("");
      setCountdown(REAUTH_TIMEOUT);
    }, IDLE_TIMEOUT);
  }, [user, showReauth]);

  // Listen for user activity
  useEffect(() => {
    if (!user) return;
    const handler = () => resetIdleTimer();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, resetIdleTimer]);

  // Countdown timer when reauth dialog is open
  useEffect(() => {
    if (!showReauth) {
      if (countdownTimer.current) clearInterval(countdownTimer.current);
      return;
    }
    setCountdown(REAUTH_TIMEOUT);
    countdownTimer.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          // Time's up — logout
          setShowReauth(false);
          logout();
          toast.error("Session expired due to inactivity");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownTimer.current) clearInterval(countdownTimer.current); };
  }, [showReauth, logout]);

  const handleReauth = async () => {
    if (!password.trim()) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      await api.post("/auth/login", { identifier: user.email || user.mobile, password });
      setShowReauth(false);
      resetIdleTimer();
      toast.success("Session extended");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={showReauth} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm [&>button]:hidden" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" /> Session Timeout
          </DialogTitle>
          <DialogDescription>
            You've been inactive for 30 minutes. Enter your password to continue or you'll be logged out.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Countdown */}
          <div className="flex items-center justify-center gap-2 text-2xl font-mono font-bold" data-testid="session-countdown">
            <Timer className={`h-5 w-5 ${countdown <= 10 ? "text-red-500 animate-pulse" : "text-amber-500"}`} />
            <span className={countdown <= 10 ? "text-red-500" : "text-foreground"}>
              {countdown}s
            </span>
          </div>

          <div>
            <Label>Password</Label>
            <Input
              type="password" autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleReauth()}
              placeholder="Enter your password"
              className="mt-1"
              data-testid="session-password"
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { setShowReauth(false); logout(); }} data-testid="session-logout-btn">
              Logout
            </Button>
            <Button className="flex-1" onClick={handleReauth} disabled={busy} data-testid="session-continue-btn">
              {busy ? "Verifying..." : "Continue session"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
